import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Row, Col, Label, Input, Button } from "reactstrap";
import { SearchSelectField } from "../ui/SearchSelect";
import { DateRangeField } from "../ui/DateRange";

/**
 * 設定式進階查詢/篩選面板（主計劃共用元件）。
 *
 * 各查詢功能只需傳入 fields 設定，不必各自刻表單：
 *   <QueryPanel
 *     fields={[
 *       { name:"keyword", label:"關鍵字", type:"text", span:4 },
 *       { name:"status",  label:"狀態",   type:"select", options:[{value:"啟用中",label:"啟用中"}] },
 *       { name:"from",    label:"起日",   type:"date" },
 *     ]}
 *     onSearch={(values)=> refetch(values)}
 *   />
 *
 * onSearch 收到已清掉空值的 query 物件，可直接餵給 dck_helper 的 list()。
 */
const buildInitial = (fields) =>
  fields.reduce((acc, f) => {
    // 多值型欄位(日期區間、多選)的空值是空陣列,不是空字串
    const blank = f.type === "date-range" || f.isMulti ? [] : "";
    acc[f.name] = f.defaultValue ?? blank;
    return acc;
  }, {});

const stripEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) =>
      v !== "" && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
  );

const QueryPanel = ({ fields, onSearch, onReset, submitLabel = "查詢" }) => {
  const [values, setValues] = useState(() => buildInitial(fields));

  const setField = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    onSearch(stripEmpty(values));
  };

  const handleReset = () => {
    const cleared = buildInitial(fields);
    setValues(cleared);
    if (onReset) onReset();
    else onSearch({});
  };

  return (
    <Card className="mb-3">
      <CardBody>
        <form onSubmit={handleSearch}>
          <Row className="g-3 align-items-end">
            {fields.map((f) => (
              <Col md={f.span || 3} key={f.name}>
                {/* 綁到欄位 —— 沒綁的話點標籤不會 focus,讀螢幕的人也聽不出這欄是什麼 */}
                <Label className="form-label mb-1" for={f.name}>{f.label}</Label>
                {f.type === "search-select" ? (
                  // 長清單篩選(機房/廠商…)可打字過濾
                  <SearchSelectField
                    name={f.name}
                    value={values[f.name]}
                    onChange={(v) => setField(f.name, v)}
                    options={f.options || []}
                    isMulti={f.isMulti}
                    placeholder={f.placeholder || "全部"}
                  />
                ) : f.type === "date-range" ? (
                  <DateRangeField
                    name={f.name}
                    value={values[f.name] || []}
                    onChange={(v) => setField(f.name, v)}
                    placeholder={f.placeholder}
                  />
                ) : f.type === "select" ? (
                  <Input
                    id={f.name}
                    type="select"
                    value={values[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                  >
                    <option value="">全部</option>
                    {(f.options || []).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Input>
                ) : (
                  <Input
                    id={f.name}
                    type={f.type || "text"}
                    placeholder={f.placeholder || ""}
                    value={values[f.name]}
                    onChange={(e) => setField(f.name, e.target.value)}
                  />
                )}
              </Col>
            ))}
            <Col md="auto">
              <div className="d-flex gap-2">
                <Button color="primary" type="submit">
                  <i className="mdi mdi-magnify me-1" />{submitLabel}
                </Button>
                <Button color="light" type="button" onClick={handleReset}>
                  清空
                </Button>
              </div>
            </Col>
          </Row>
        </form>
      </CardBody>
    </Card>
  );
};

QueryPanel.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      options: PropTypes.array,
      span: PropTypes.number,
      placeholder: PropTypes.string,
      defaultValue: PropTypes.any,
    })
  ).isRequired,
  onSearch: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  submitLabel: PropTypes.string,
};

export default QueryPanel;
